import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { jobAPI } from '../api/api';
import { useAuth } from '../context/AuthContext';
import './JobRequests.css';
import { onayla } from '../utils/onayla';

const JobRequests = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectionReason, setRejectionReason] = useState('');
  const [newDueDate, setNewDueDate] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(null);

  const isAdmin = () => {
    if (!user) return false;
    const role = user.Role ?? user.role ?? '';
    return role === 'Admin';
  };

  useEffect(() => {
    if (isAdmin()) {
      loadRequests();
    }
  }, [user]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const data = await jobAPI.getPendingRequests();
      setRequests(data);
    } catch (error) {
      console.error('İş talepleri yüklenirken hata:', error);
      alert('İş talepleri yüklenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    if (!await onayla('Bu iş talebini onaylamak istediğinize emin misiniz?')) {
      return;
    }

    try {
      await jobAPI.approve(id);
      alert('İş talebi onaylandı.');
      await loadRequests();
    } catch (error) {
      console.error('İş talebi onaylanırken hata:', error);
      alert('İş talebi onaylanırken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleReject = async (id) => {
    if (!rejectionReason.trim()) {
      alert('Lütfen red nedeni girin!');
      return;
    }

    try {
      await jobAPI.reject(id, rejectionReason, newDueDate || null);
      alert('İş talebi reddedildi.');
      setShowRejectModal(null);
      setRejectionReason('');
      setNewDueDate('');
      await loadRequests();
    } catch (error) {
      console.error('İş talebi reddedilirken hata:', error);
      alert('İş talebi reddedilirken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (!isAdmin()) {
    return (
      <div className="job-requests-page">
        <div className="page-header">
          <h1>İş Talepleri</h1>
        </div>
        <div className="error-message">
          <p>Bu sayfaya sadece Admin erişebilir.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="job-requests-page">
      <div className="page-header">
        <h1>İş Talepleri</h1>
      </div>

      {loading ? (
        <div className="loading">Yükleniyor...</div>
      ) : requests.length === 0 ? (
        <div className="empty-state">
          <p>Bekleyen iş talebi bulunmamaktadır.</p>
        </div>
      ) : (
        <div className="requests-table-container">
          <table className="requests-table">
            <thead>
              <tr>
                <th>Föy No</th>
                <th>Müvekkil</th>
                <th>İş Açıklaması</th>
                <th>Yapan Kişi</th>
                <th>Talep Tarihi</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {requests.map(request => {
                const caseFileId = request.CaseFileId || request.caseFileId;
                const caseFileFoyNo = request.CaseFileFoyNo || request.caseFileFoyNo;
                return (
                <tr key={request.Id || request.id}>
                  <td>
                    {caseFileFoyNo && caseFileId ? (
                      <Link 
                        to={`/case-files/${caseFileId}`}
                        style={{ color: '#667eea', textDecoration: 'underline', cursor: 'pointer' }}
                      >
                        {caseFileFoyNo}
                      </Link>
                    ) : (
                      caseFileFoyNo || '-'
                    )}
                  </td>
                  <td>{request.CaseFileMuvekkil || request.caseFileMuvekkil || '-'}</td>
                  <td>{request.Description || request.description || '-'}</td>
                  <td>
                    {request.CompletedByUserName || request.completedByUserName || ''} {request.CompletedByUserSurname || request.completedByUserSurname || ''}
                  </td>
                  <td>{formatDate(request.RequestedDate || request.requestedDate)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => handleApprove(request.Id || request.id)}
                      >
                        Onayla
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => setShowRejectModal(request.Id || request.id)}
                      >
                        Reddet
                      </button>
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Reddetme Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => {
          setShowRejectModal(null);
          setRejectionReason('');
          setNewDueDate('');
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>İş Talebini Reddet</h3>
            <div className="form-group">
              <label>Red Nedeni *</label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows="4"
                placeholder="Red nedeni girin..."
                required
              />
            </div>
            <div className="form-group">
              <label>Yeni Son Tarih (Opsiyonel)</label>
              <input
                type="date"
                value={newDueDate}
                onChange={(e) => setNewDueDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
              <small style={{ display: 'block', marginTop: '5px', color: '#666', fontSize: '12px' }}>
                İş reddedildikten sonra yeni bir son tarih belirleyebilirsiniz.
              </small>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-primary"
                onClick={() => handleReject(showRejectModal)}
              >
                Reddet
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowRejectModal(null);
                  setRejectionReason('');
                  setNewDueDate('');
                }}
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobRequests;

